import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FilterOption } from '../../../types/FilterOption';

import { NetworkStatus } from '@apollo/client/core';
import { useLazyQuery, useQuery } from '@apollo/client/react';

import { stylesFilterResultsLimit } from '../../../components/PurchaseOrdersComponents/StylesFilter/constants';
import { GetStyleIdentifiersDocument } from '../../../gql';
import { GraphClientNames } from '../../../types/GQLClientNames';
import { isDefined } from '../../../utils/filter-util';

interface UseStylesFilterParams {
    collectionNumbers: Array<number>;
    activeStyleOptions: Array<FilterOption>;
}

interface UseStylesFilterResult {
    /** The merged options: active selections + current search results */
    stylesOptions: Array<FilterOption>;
    /** True from when the search query changes until the debounced query finishes loading */
    isSearching: boolean;
    /** Setter to update the search query (debounced internally before querying) */
    setSearchQuery: Dispatch<SetStateAction<string>>;
    /** The current (non-debounced) search query value */
    searchQuery: string;
    /** Total count of styles matching the current search (including beyond limit). */
    totalCount: number;
    /** The number of options currently displayed in the dropdown */
    optionsCount: number;
    /** Fetches all matching styles (ignoring limit) and returns them as FilterOption[] */
    insertAllStyles: () => Promise<Array<FilterOption>>;
}

/**
 * Fetches style options for filter bar usage, with debounced search.
 * Queries the style BFF for styles matching the given collections and search query.
 *
 * @param collectionNumbers - Collection numbers to scope the style search to.
 * @param activeStyleOptions - Currently selected style options (used to retain them in the options list).
 * @returns stylesOptions - Merged filter options (active selections + current results).
 * @returns isSearching - True while waiting for debounced query to resolve.
 * @returns setSearchQuery - Setter to update the search input value.
 */
const useStylesFilter = ({
    collectionNumbers,
    activeStyleOptions,
}: UseStylesFilterParams): UseStylesFilterResult => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    const { data: stylesData, networkStatus } = useQuery(
        GetStyleIdentifiersDocument,
        {
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
            skip: collectionNumbers.length === 0 || debouncedSearchQuery === '',
            context: {
                clientName: GraphClientNames['bestone-bi4-buying-style-bff'],
            },
            variables: {
                collectionNumbers,
                generalQuery: debouncedSearchQuery,
                limit: stylesFilterResultsLimit,
            },
        }
    );

    const [fetchAllStyles] = useLazyQuery(GetStyleIdentifiersDocument);
    const totalCount = stylesData?.stylesLookup.totalCount ?? 0;
    const isSearching =
        searchQuery !== debouncedSearchQuery ||
        networkStatus === NetworkStatus.setVariables;

    // Map current query results to FilterOption format
    const currentResults: Array<FilterOption> = useMemo(() => {
        const styles = stylesData?.stylesLookup.styles;

        return (styles ?? [])
            .filter(isDefined)
            .filter(
                (
                    style
                ): style is { styleName: string | null; styleNumber: number } =>
                    isDefined(style.styleNumber)
            )
            .map((style) => ({
                label: `${style.styleNumber} - ${style.styleName ?? ''}`,
                value: String(style.styleNumber),
            }));
    }, [stylesData]);

    // No search: show all active options for deselection.
    // Searching (debouncing): empty (loading state).
    // Search resolved: show results + active options that match the search
    // but weren't returned (they exist past the 20 limit).
    const { stylesOptions, optionsCount } = useMemo(() => {
        if (isSearching) return { stylesOptions: [], optionsCount: 0 };
        if (debouncedSearchQuery === '') {
            return {
                stylesOptions: [...activeStyleOptions].sort(
                    (firstOption, secondOption) =>
                        Number(firstOption.value) - Number(secondOption.value)
                ),
                optionsCount: activeStyleOptions.length,
            };
        }

        const currentResultValues = new Set(
            currentResults.map((option) => option.value)
        );
        const normalizedSearchQuery = debouncedSearchQuery.toLowerCase();
        const matchingActiveOptions = activeStyleOptions.filter(
            (option) =>
                !currentResultValues.has(option.value) &&
                (option.value.includes(normalizedSearchQuery) ||
                    option.label.toLowerCase().includes(normalizedSearchQuery))
        );

        return {
            stylesOptions: [...currentResults, ...matchingActiveOptions].sort(
                (firstOption, secondOption) =>
                    Number(firstOption.value) - Number(secondOption.value)
            ),
            optionsCount: currentResults.length + matchingActiveOptions.length,
        };
    }, [isSearching, debouncedSearchQuery, activeStyleOptions, currentResults]);

    // Debounce the search query
    useEffect((): (() => void) => {
        const timeoutId = globalThis.setTimeout((): void => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return (): void => {
            globalThis.clearTimeout(timeoutId);
        };
    }, [searchQuery]);

    const insertAllStyles = useCallback(async (): Promise<
        Array<FilterOption>
    > => {
        const result = await fetchAllStyles({
            context: {
                clientName: GraphClientNames['bestone-bi4-buying-style-bff'],
            },
            variables: {
                collectionNumbers,
                generalQuery: debouncedSearchQuery,
            },
        });

        return (result.data?.stylesLookup.styles ?? [])
            .filter(isDefined)
            .filter((style) => isDefined(style.styleNumber))
            .map((style) => ({
                label: `${style.styleNumber} - ${style.styleName ?? ''}`,
                value: String(style.styleNumber),
            }));
    }, [fetchAllStyles, collectionNumbers, debouncedSearchQuery]);

    return {
        stylesOptions,
        isSearching,
        setSearchQuery,
        searchQuery,
        totalCount,
        optionsCount,
        insertAllStyles,
    };
};

export { useStylesFilter };
